import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box, Card, Typography, Chip, Stack, Button,
  Select, MenuItem, FormControl, InputLabel,
  ToggleButtonGroup, ToggleButton, useTheme, useMediaQuery
} from "@mui/material";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import PaymentsIcon from "@mui/icons-material/Payments";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import DeleteIcon from "@mui/icons-material/Delete";
import { PageHeader } from "../components/PageHeader.js";
import { EmptyState } from "../components/EmptyState.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { SettledChip } from "../components/StatusChip.js";
import { RowQuickActions } from "../components/RowQuickActions.js";
import { ComboPartners } from "../components/combos/index.js";
import { useApi } from "../hooks/useApi.js";
import { useReceiptWhatsApp } from "../hooks/useReceiptWhatsApp.js";
import { useReceiptsFilterStore } from "../store/receiptsFilterStore.js";
import type { Receipt, PartnerReduced } from "@kappa-v2/contracts";
import { format } from "date-fns";

const eur = (n?: number) => `€ ${(n ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;
// period = "MM/YYYY"
const yearOf = (period: string) => period.split("/")[1] ?? "";
// Valore ordinabile cronologico (YYYY*100 + MM) per ordinare dal più recente al più vecchio.
const periodRank = (period: string) => {
  const [mm, yyyy] = period.split("/");
  return (Number(yyyy) || 0) * 100 + (Number(mm) || 0);
};

type Mode = "unpaid" | "paid" | "all";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR + 2 - 2024 + 1 }, (_, i) => `${2024 + i}`);

const PARTNER_TYPE_KEYS = [
  "form.partnerType.pony",
  "form.partnerType.horse",
  "form.partnerType.owner",
  "form.partnerType.other"
];
// Ordine coerente coi filtri a toggle del resto dell'app: Proprietario → Cavallo → Pony.
const TYPE_FILTER = ["form.partnerType.owner", "form.partnerType.horse", "form.partnerType.pony"];

export function ReceiptsPage() {
  const { t } = useTranslation();
  const { call, openPdf, printPdf } = useApi();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [rows, setRows] = useState<Receipt[]>([]);
  const [partners, setPartners] = useState<PartnerReduced[]>([]);
  const [loading, setLoading] = useState(true);

  // Filtri inizializzati dallo store (persistono tra le navigazioni: dettaglio → indietro).
  // Un eventuale ?personId nell'URL ha priorità (es. arrivo dalla scheda socio).
  const persistFilters = useReceiptsFilterStore((s) => s.set);
  const savedFilters = useState(() => useReceiptsFilterStore.getState())[0];
  const [socioFilter, setSocioFilter] = useState(searchParams.get("personId") ?? savedFilters.socioFilter);
  const [year, setYear] = useState(savedFilters.year || `${CURRENT_YEAR}`);
  const [mode, setMode] = useState<Mode>((savedFilters.mode as Mode) || "unpaid");
  const [activeFilter, setActiveFilter] = useState<"all" | "true" | "false">(
    (savedFilters.activeFilter as "all" | "true" | "false") || "all"
  );
  const [typeFilter, setTypeFilter] = useState(savedFilters.typeFilter);
  const [deleteTarget, setDeleteTarget] = useState<Receipt | null>(null);
  const [waTarget, setWaTarget] = useState<Receipt | null>(null);
  const sendReceiptWa = useReceiptWhatsApp();

  // Salva i filtri nello store ad ogni cambio → restano al ritorno dal dettaglio.
  useEffect(() => {
    persistFilters({ socioFilter, year, mode, activeFilter, typeFilter });
  }, [socioFilter, year, mode, activeFilter, typeFilter, persistFilters]);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      call<{ data: Receipt[] } | Receipt[]>("/api/v1/payments/receipts"),
      call<{ data: PartnerReduced[] } | PartnerReduced[]>("/api/v1/partners/reduced?active=all")
    ]).then(([rRes, pRes]) => {
      if (rRes.status === "fulfilled") {
        const v = rRes.value;
        setRows(Array.isArray(v) ? v : (v.data ?? []));
      }
      if (pRes.status === "fulfilled") {
        const v = pRes.value;
        setPartners(Array.isArray(v) ? v : (v.data ?? []));
      }
    }).finally(() => setLoading(false));
  }, [call]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const partnerById = useMemo(() => {
    const m = new Map<string, PartnerReduced>();
    partners.forEach((p) => p._id && m.set(String(p._id), p));
    return m;
  }, [partners]);

  const partnerName = (id: string) =>
    partnerById.get(id)?.fullName ?? id;

  const filtered = useMemo(() => {
    const list = rows.filter((r) => {
      if (socioFilter && r.personId !== socioFilter) return false;
      const p = partnerById.get(r.personId);
      if (activeFilter !== "all" && String(p?.active) !== activeFilter) return false;
      if (typeFilter && p?.partnerType !== typeFilter) return false;
      // Come in v1: "Non saldate" mostra TUTTE le scoperte (qualsiasi anno) con dovuto > 0;
      // "Saldate"/"Tutte" filtrano per l'anno selezionato.
      if (mode === "unpaid") return !r.settled && r.totalCredit > 0;
      if (yearOf(r.period) !== year) return false;
      if (mode === "paid") return r.settled;
      return true;
    });
    // Ordine per periodo decrescente (dal più recente al più vecchio).
    return [...list].sort((a, b) => periodRank(b.period) - periodRank(a.period));
  }, [rows, partnerById, socioFilter, activeFilter, typeFilter, mode, year]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget?._id) return;
    await call(`/api/v1/payments/receipts/${deleteTarget._id}`, { method: "DELETE" });
    setDeleteTarget(null);
    fetchAll();
  }, [call, deleteTarget, fetchAll]);

  const buildReceiptActions = (r: Receipt) => {
    const pdfPath = `/api/v1/payments/receipts/${r._id}/pdf`;
    const detail = `/protected/receipt/${r._id}/partner/${r.personId}/period/${encodeURIComponent(r.period)}`;
    return [
      { key: "open", label: t("receipts.actions.open"), description: t("receipts.actions.openDesc"), icon: <OpenInNewIcon fontSize="small" />, onClick: () => navigate(detail) },
      { key: "print", label: t("receipts.actions.print"), description: t("receipts.actions.printDesc"), icon: <PrintIcon fontSize="small" />, onClick: () => printPdf(pdfPath) },
      { key: "pdf", label: t("receipts.actions.pdf"), description: t("receipts.actions.pdfDesc"), icon: <PictureAsPdfIcon fontSize="small" />, onClick: () => openPdf(pdfPath) },
      { key: "whatsapp", label: t("receipts.actions.whatsapp"), description: t("receipts.actions.whatsappDesc"), icon: <WhatsAppIcon fontSize="small" />, onClick: () => setWaTarget(r) },
      { key: "credits", label: t("receipts.actions.credits"), description: t("receipts.actions.creditsDesc"), icon: <RequestQuoteIcon fontSize="small" />, onClick: () => navigate(`/protected/credits?personId=${r.personId}`) },
      { key: "debits", label: t("receipts.actions.debits"), description: t("receipts.actions.debitsDesc"), icon: <PaymentsIcon fontSize="small" />, onClick: () => navigate(`/protected/debits?personId=${r.personId}`) },
      { key: "delete", label: t("receipts.actions.delete"), description: t("receipts.actions.deleteDesc"), icon: <DeleteIcon fontSize="small" />, destructive: true, dividerBefore: true, onClick: () => setDeleteTarget(r) }
    ];
  };

  const columns: GridColDef[] = [
    {
      field: "personId", headerName: t("receipts.socio"), flex: 1, minWidth: 200,
      renderCell: (params: GridRenderCellParams<Receipt>) => {
        const p = partnerById.get(params.row.personId);
        const inactive = p?.active === false;
        return (
          <Typography variant="body2" fontWeight={600} color={inactive ? "error.main" : "text.primary"}>
            {inactive ? "🚫 " : ""}{partnerName(params.row.personId)}
          </Typography>
        );
      }
    },
    {
      field: "period", headerName: t("common.period"), width: 110,
      renderCell: (params: GridRenderCellParams<Receipt>) => (
        <Chip size="small" label={params.row.period} variant="outlined" sx={{ fontSize: "0.72rem" }} />
      )
    },
    {
      field: "totalCredit", headerName: t("receipts.due"), width: 120, align: "right", headerAlign: "right",
      renderCell: (params: GridRenderCellParams<Receipt>) => (
        <Typography variant="body2" fontWeight={700}>{eur(params.row.totalCredit)}</Typography>
      )
    },
    {
      field: "totalDebit", headerName: t("receipts.paidAmount"), width: 120, align: "right", headerAlign: "right",
      renderCell: (params: GridRenderCellParams<Receipt>) => (
        <Typography variant="body2" fontWeight={700} color="success.dark">{eur(params.row.totalDebit)}</Typography>
      )
    },
    {
      field: "settled", headerName: t("receipts.settled"), width: 120,
      renderCell: (params: GridRenderCellParams<Receipt>) => <SettledChip settled={params.row.settled} />
    },
    {
      field: "datePayment", headerName: t("receipts.lastPayment"), width: 150,
      renderCell: (params: GridRenderCellParams<Receipt>) => (
        <Typography variant="body2" color="text.secondary">
          {params.row.datePayment ? format(new Date(params.row.datePayment), "dd/MM/yyyy") : "—"}
        </Typography>
      )
    },
    {
      field: "actions", headerName: "", width: 160, sortable: false, align: "right", headerAlign: "center",
      renderCell: (params: GridRenderCellParams<Receipt>) => (
        <RowQuickActions
          ariaLabel={`Azioni su ricevuta #${params.row.receiptsNumber}`}
          quickKeys={["open", "print", "whatsapp"]}
          items={buildReceiptActions(params.row)}
        />
      )
    }
  ];

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <PageHeader title={t("receipts.title")} subtitle={t("receipts.subtitle")} onRefresh={fetchAll} />
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, flex: 1 }}>
        <Card>
          <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,.06)" }}>
            <Box sx={{ mb: 2 }} data-tour="rc-socio">
              <ComboPartners label={t("receipts.socio")} value={socioFilter} onChange={setSocioFilter} active={activeFilter} />
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
              <FormControl size="small" sx={{ minWidth: 120 }} data-tour="rc-year">
                <InputLabel>{t("common.year")}</InputLabel>
                <Select label={t("common.year")} value={year} onChange={(e) => setYear(e.target.value)}>
                  {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </Select>
              </FormControl>
              <ToggleButtonGroup
                data-tour="rc-mode"
                size="small" color="primary" exclusive
                value={mode}
                onChange={(_, v) => v !== null && setMode(v as Mode)}
              >
                <ToggleButton value="all">{t("receipts.allReceipts")}</ToggleButton>
                <ToggleButton value="unpaid">{t("receipts.unpaid")}</ToggleButton>
                <ToggleButton value="paid">{t("receipts.paid")}</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup
                data-tour="rc-active"
                size="small" color="primary" exclusive
                value={activeFilter}
                onChange={(_, v) => v !== null && setActiveFilter(v as "all" | "true" | "false")}
              >
                <ToggleButton value="all">{t("common.all")}</ToggleButton>
                <ToggleButton value="true">{t("receipts.activePartners")}</ToggleButton>
                <ToggleButton value="false">{t("receipts.inactivePartners")}</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup
                data-tour="rc-type"
                size="small" color="primary" exclusive
                value={typeFilter}
                onChange={(_, v) => v !== null && setTypeFilter(v)}
              >
                <ToggleButton value="">{t("common.all")}</ToggleButton>
                {TYPE_FILTER.map((k) => <ToggleButton key={k} value={k}>{t(k)}</ToggleButton>)}
              </ToggleButtonGroup>
              <Box sx={{ flex: 1 }} />
              {socioFilter && (
                <Button color="inherit" size="small" onClick={() => setSocioFilter("")}>
                  {t("receipts.allPartners")}
                </Button>
              )}
            </Stack>
          </Box>
          {!loading && filtered.length === 0
            ? <EmptyState title={t("receipts.none")} description={t("receipts.noneDesc")} />
            : isMobile
              ? (
                <Box>
                  {filtered.map((r, idx) => {
                    const p = partnerById.get(r.personId);
                    const inactive = p?.active === false;
                    return (
                      <Box key={r._id ?? idx} sx={{ p: 2, borderTop: idx ? "1px solid rgba(0,0,0,.06)" : "none" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={700} color={inactive ? "error.main" : "text.primary"}>
                              {inactive ? "🚫 " : ""}{partnerName(r.personId)}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap", alignItems: "center" }}>
                              <Chip size="small" label={r.period} variant="outlined" sx={{ fontSize: "0.72rem" }} />
                              <SettledChip settled={r.settled} />
                            </Stack>
                          </Box>
                          <RowQuickActions
                            ariaLabel={`Azioni su ricevuta #${r.receiptsNumber}`}
                            quickKeys={["open", "print", "whatsapp"]}
                            items={buildReceiptActions(r)}
                          />
                        </Box>
                        <Stack direction="row" spacing={3} sx={{ mt: 1.5, flexWrap: "wrap" }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">{t("receipts.due")}</Typography>
                            <Typography variant="body2" fontWeight={700}>{eur(r.totalCredit)}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">{t("receipts.paidAmount")}</Typography>
                            <Typography variant="body2" fontWeight={700} color="success.dark">{eur(r.totalDebit)}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">{t("receipts.lastPayment")}</Typography>
                            <Typography variant="body2">
                              {r.datePayment ? format(new Date(r.datePayment), "dd/MM/yyyy") : "—"}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
              )
              : <DataGrid rows={filtered} columns={columns} getRowId={(r) => r._id ?? Math.random().toString()}
                  loading={loading} autoHeight rowHeight={56} pageSizeOptions={[25, 50, 100]}
                  initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
                  disableRowSelectionOnClick sx={{
                    border: "none",
                    "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
                    "& .MuiDataGrid-cell--textRight": { justifyContent: "flex-end" },
                    "& .MuiDataGrid-cell--textLeft": { justifyContent: "flex-start" },
                    "& .MuiDataGrid-cell--textCenter": { justifyContent: "center" }
                  }}
                />
          }
        </Card>
      </Box>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        message={t("receipts.deleteConfirm", { number: deleteTarget?.receiptsNumber })}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(waTarget)}
        title={t("receipts.wa.confirmTitle")}
        confirmLabel={t("receipts.wa.confirmSend")}
        confirmColor="success"
        message={t("receipts.wa.confirm", { name: waTarget ? partnerName(waTarget.personId) : "", period: waTarget?.period })}
        onConfirm={() => {
          if (waTarget) {
            const p = partnerById.get(waTarget.personId);
            void sendReceiptWa({
              receiptId: String(waTarget._id),
              partnerId: waTarget.personId,
              fullName: p?.fullName ?? "",
              cells: p?.cells ?? [],
              period: waTarget.period,
              settled: Boolean(waTarget.settled)
            });
          }
          setWaTarget(null);
        }}
        onCancel={() => setWaTarget(null)}
      />
    </Box>
  );
}
